import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FrontloadingPhase from './FrontloadingPhase';
import LCVProgressTracker from './LCVProgressTracker';
import LCVStageTable from './LCVStageTable';
import LCVTutorDialog from './LCVTutorDialog';
import LCVTopBar from './LCVTopBar';
import LCVViewBoard from './LCVViewBoard';

const DISCLAIMER_KEY = 'lcv-frontloading-disclaimer-dismissed';
const ENABLE_FRONTLOADING_LISTENING = true;

const LCVFrontloadingStage = ({
  data,
  tutorText,
  metadata,
  progressData,
  phase,
  trackerMode,
  visualContent,
  startDisabled = false,
  onStart,
  paused = false,
  tutorAnimationProgress = 0,
  tutorWordTimings = null,
  tutorCurrentTimeMs = 0,
  tutorFontSize,
  onTutorFontSizeChange,
  onPauseTutor,
  onResumeTutor,
  onSkipTutor,
  playbackRate = 1,
  canIncreasePlaybackRate = false,
  canDecreasePlaybackRate = false,
  onIncreasePlaybackRate,
  onDecreasePlaybackRate,
  volume = 100,
  canIncreaseVolume = false,
  canDecreaseVolume = false,
  onIncreaseVolume,
  onDecreaseVolume,
  isMuted = false,
  onToggleMute,
  canPauseTutor = false,
  canSkipTutor = false,
  tutorPlaybackFinished = false,
  onNavigateTracker,
}) => {
  const termCount = data?.terms?.length || 0;
  const [activeIndex, setActiveIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    try {
      return !sessionStorage.getItem(DISCLAIMER_KEY);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    setActiveIndex(0);
    setFinished(!ENABLE_FRONTLOADING_LISTENING);
  }, [data?.topic, termCount]);

  const handleDismissDisclaimer = useCallback(() => {
    setShowDisclaimer(false);
    try {
      sessionStorage.setItem(DISCLAIMER_KEY, '1');
    } catch {
      // noop
    }
  }, []);

  const handleTutorFinished = useCallback(() => {
    if (termCount > 0) {
      setActiveIndex(termCount - 1);
    }
    setFinished(true);
  }, [termCount]);

  useEffect(() => {
    if (!ENABLE_FRONTLOADING_LISTENING || finished || termCount <= 0) return;

    const nextIndex = Math.min(
      termCount - 1,
      Math.floor(Math.max(0, Math.min(1, tutorAnimationProgress)) * termCount),
    );

    setActiveIndex(nextIndex);
  }, [finished, termCount, tutorAnimationProgress]);

  const tutorLabel = useMemo(() => {
    if (termCount === 0) return '';
    return `${Math.min(activeIndex + 1, termCount)} of ${termCount}`;
  }, [activeIndex, termCount]);

  if (!data || !progressData) return null;

  return (
    <LCVStageTable
      className="lcv-stage-table-frontloading"
      topLeft={<LCVTopBar metadata={metadata} />}
      topCenter={
        <LCVProgressTracker
          data={progressData}
          phase={phase}
          mode={trackerMode}
          onNavigate={onNavigateTracker}
        />
      }
      topRight={<div className="lcv-stage-empty" />}
      middleLeft={
        visualContent ? (
          <LCVViewBoard phase={phase} visualContent={visualContent} />
        ) : (
          <div className="lcv-stage-empty" />
        )
      }
      middleCenter={
        <div className="lcv-stage-middle-shell lcv-stage-middle-shell-frontloading">
          <FrontloadingPhase
            data={data}
            activeIndex={activeIndex}
            finished={finished}
            startDisabled={startDisabled}
            onStart={onStart}
            showDisclaimer={showDisclaimer}
            onDismissDisclaimer={handleDismissDisclaimer}
            listeningEnabled={ENABLE_FRONTLOADING_LISTENING}
            showRestart={false}
          />
        </div>
      }
      middleRight={<div className="lcv-stage-empty" />}
      bottomLeft={<div className="lcv-stage-empty" />}
      bottom={
        <LCVTutorDialog
          text={tutorText || data.tutorScript}
          label={tutorLabel}
          animated={ENABLE_FRONTLOADING_LISTENING}
          progressRatio={tutorAnimationProgress}
          wordTimings={tutorWordTimings}
          currentTimeMs={tutorCurrentTimeMs}
          paused={paused}
          onFinished={handleTutorFinished}
          showControls
          canPause={canPauseTutor}
          canSkip={canSkipTutor}
          fontSize={tutorFontSize}
          onFontSizeChange={onTutorFontSizeChange}
          onPause={onPauseTutor}
          onResume={onResumeTutor}
          onSkip={onSkipTutor}
          playbackRate={playbackRate}
          canIncreasePlaybackRate={canIncreasePlaybackRate}
          canDecreasePlaybackRate={canDecreasePlaybackRate}
          onIncreasePlaybackRate={onIncreasePlaybackRate}
          onDecreasePlaybackRate={onDecreasePlaybackRate}
          volume={volume}
          canIncreaseVolume={canIncreaseVolume}
          canDecreaseVolume={canDecreaseVolume}
          onIncreaseVolume={onIncreaseVolume}
          onDecreaseVolume={onDecreaseVolume}
          isMuted={isMuted}
          onToggleMute={onToggleMute}
          playbackFinished={tutorPlaybackFinished}
        />
      }
    />
  );
};

export default LCVFrontloadingStage;
